import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { Popover, message } from "antd"

import TextInput from "../../../../_shared/components/FormItems/AppInput"
import UOMInput from "../../../../_shared/components/FormItems/UOMInput"

import AppSwitch from "../../../../_shared/components/FormItems/AppSwitch"
import AppButton from "../../../../_shared/components/AppButton"
import NewTokensForm from "../../../../_shared/components/NewTokensForm"

// ----------------------------------------------------------------------------

function CreateUserForm(props) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const { onSubmit, user, onCreateNewTokens } = props

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const [email, setEmail] = useState("")
  const [companyName, setCompanyName] = useState("")
  const [plantCode, setPlantCode] = useState("")
  const [bleDevices, setBleDevices] = useState("")
  const [tokenNumber, setTokenNumber] = useState(0)
  const [multiDeviceWaitTime, setMultiDeviceWaitTime] = useState(0)
  const [automaticSendCode, setAutomaticSendCode] = useState(false)
  const [nfcCapabilityEnabled, setNfcCapabilityEnabled] = useState(true)
  const [reasonCapabilityEnabled, setReasonCapabilityEnabled] = useState(false)
  const [presenceCapabilityEnabled, setPresenceCapabilityEnabled] = useState(
    true
  )
  const [accessCapabilityEnabled, setAccessCapabilityEnabled] = useState(true)
  const [terminalResponseWaitTime, setTerminalResponseWaitTime] = useState(1)
  const [resetPassword, setResetPassword] = useState("")
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState("")

  const [isPopoverVisible, setIsPopoverVisible] = useState(false)

  // -------------------------------------
  // Effects
  // -------------------------------------

  useEffect(() => {
    if (user && user.id) {
      setEmail(user.email)
      setCompanyName(user.companyName)
      setPlantCode(user.plantCode)
      setBleDevices(user.bleDevices)
      setMultiDeviceWaitTime(user.multiDeviceWaitTime)
      setAutomaticSendCode(user.automaticSendCode)
      setNfcCapabilityEnabled(user.nfcCapabilityEnabled)
      setReasonCapabilityEnabled(user.reasonCapabilityEnabled)
      setPresenceCapabilityEnabled(user.presenceCapabilityEnabled)
      setAccessCapabilityEnabled(user.accessCapabilityEnabled)
      setTerminalResponseWaitTime(user.terminalResponseWaitTime)
      setResetPassword("")
      setResetPasswordConfirm("")
    } else {
      clearState()
    }
  }, [user])

  // -------------------------------------
  // Component functions
  // -------------------------------------

  async function handleSubmit(event) {
    event.preventDefault()

    let body = {
      email,
      companyName,
      tokenNumber,
      plantCode,
      bleDevices,
      multiDeviceWaitTime,
      automaticSendCode,
      nfcCapabilityEnabled,
      reasonCapabilityEnabled,
      presenceCapabilityEnabled,
      accessCapabilityEnabled,
    }

    if (resetPassword !== resetPasswordConfirm) {
      message.error("Reimposta password e Conferma password devono coincidere")
      return
    }
    if (resetPassword.lenth > 0 && resetPassword.length < 6) {
      message.error("La password deve avere almeno 6 caratteri")
      return
    }

    body.resetPassword = resetPassword
    body.resetPasswordConfirm = resetPasswordConfirm

    try {
      onSubmit && (await onSubmit(body))

      clearState()

      //eslint-disable-next-line
    } catch (err) {}
  }

  function handleCreateNewTokens(number) {
    if (number) {
      onCreateNewTokens && onCreateNewTokens(user.id, number)

      setIsPopoverVisible(false)
    }
  }

  function handlePopoverVisibilityChange(visible) {
    setIsPopoverVisible(visible)
  }

  function clearState() {
    setEmail("")
    setCompanyName("")
    setPlantCode("")
    setBleDevices("")
    setTokenNumber("")
    setMultiDeviceWaitTime(0)
    setAutomaticSendCode(false)
    setReasonCapabilityEnabled(true)
    setNfcCapabilityEnabled(true)
    setPresenceCapabilityEnabled(true)
    setAccessCapabilityEnabled(true)
    setTerminalResponseWaitTime(1)
    setResetPassword("")
    setResetPasswordConfirm("")
  }

  function isEditMode() {
    return user && user.id
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  return (
    <form className={props.className} onSubmit={handleSubmit}>
      <h1>{`${user && user.id ? "Modifica" : "Crea una nuova azienda"}`}</h1>

      <div className="form-content-container">
        <h4>{`Dati del cliente`}</h4>
        <TextInput
          className="form-input"
          placeholder="Email"
          value={email}
          onChange={setEmail}
        />

        <TextInput
          className="form-input"
          placeholder="Ragione sociale"
          value={companyName}
          onChange={setCompanyName}
        />
        <TextInput
          className="form-input"
          placeholder="Codice impianto"
          value={plantCode}
          onChange={setPlantCode}
        />
        <TextInput
          className="form-input"
          placeholder="Dispositivi BLE"
          value={bleDevices}
          onChange={setBleDevices}
        />

        {(!user || !user.id) && (
          <TextInput
            className="form-input"
            placeholder="Numero badge disponibili"
            value={tokenNumber}
            onChange={setTokenNumber}
          />
        )}

        <h4>{`Impostazioni dell'applicazione`}</h4>

        <UOMInput
          className="form-input"
          placeholder="Tempo risposta terminale"
          value={terminalResponseWaitTime}
          onChange={setTerminalResponseWaitTime}
          unitOfMeasurement={"s"}
        />

        <UOMInput
          className="form-input"
          placeholder="Tempo attesa multi device"
          value={multiDeviceWaitTime}
          onChange={setMultiDeviceWaitTime}
          unitOfMeasurement={"s"}
        />
        <AppSwitch
          className="form-switch"
          placeholder="Abilita terminali controllo presenze"
          value={presenceCapabilityEnabled}
          onChange={setPresenceCapabilityEnabled}
        />
        <AppSwitch
          className="form-switch"
          placeholder="Abilita terminali entrata/uscita"
          value={accessCapabilityEnabled}
          onChange={setAccessCapabilityEnabled}
        />
        <AppSwitch
          className="form-switch"
          placeholder="Abilita terminali NFC"
          value={nfcCapabilityEnabled}
          onChange={setNfcCapabilityEnabled}
        />
        <AppSwitch
          className="form-switch"
          placeholder="Abilita causali"
          value={reasonCapabilityEnabled}
          onChange={setReasonCapabilityEnabled}
        />
        <AppSwitch
          className="form-switch"
          placeholder="Invio codice automatico"
          value={automaticSendCode}
          onChange={setAutomaticSendCode}
        />

        {isEditMode() && <h4></h4>}
        {isEditMode() && <h4>{`Reimposta password Admin`}</h4>}
        {isEditMode() && (
          <UOMInput
            className="form-input"
            placeholder="Reimposta password"
            value={resetPassword}
            onChange={setResetPassword}
            hiddenText
            // unitOfMeasurement={"s"}
          />
        )}
        {isEditMode() && (
          <UOMInput
            className="form-input"
            placeholder="Conferma password"
            value={resetPasswordConfirm}
            onChange={setResetPasswordConfirm}
            hiddenText
            // unitOfMeasurement={"s"}
          />
        )}
      </div>

      <div className="btns-container">
        {isEditMode() && (
          <Popover
            content={
              <NewTokensForm
                onSubmit={handleCreateNewTokens}
                visible={isPopoverVisible}
                inputPlaceholder="Numero badge da assegnare"
              />
            }
            title="Assegna nuovi badge all'utente"
            trigger="click"
            visible={isPopoverVisible}
            onVisibleChange={handlePopoverVisibilityChange}
          >
            <AppButton
              type="button"
              style="ghosted"
              className="margined-btn new-tokens-btn"
            >
              Assegna nuovi badge
            </AppButton>
          </Popover>
        )}

        <AppButton className="form-btn">{`${
          user && user.id ? "Modifica" : "Crea"
        }`}</AppButton>
      </div>
    </form>
  )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

CreateUserForm.propTypes = {
  className: PropTypes.string.isRequired,
  onSubmit: PropTypes.func,
  onCreateNewTokens: PropTypes.func,
  user: PropTypes.object,
}

CreateUserForm.defaultProps = {
  onSubmit: () => {},
  onCreateNewTokens: () => {},
}

// ----------------------------------------------------------------------------

const StyledCreateUserForm = styled(CreateUserForm)`
  & {
    display: flex;
    flex-direction: column;
    align-items: center;

    padding-top: 50px;
    box-sizing: border-box;

    height: 100%;
    width: 100%;

    > h1 {
      font-weight: bold;
    }

    .form-content-container {
      flex: 1;

      display: flex;
      flex-direction: column;
      align-items: center;

      > h4 {
        padding: 3px 0;

        border-bottom: 1px solid ${(props) => props.theme.UITheme.textLight};
        font-weight: 600;
      }

      .form-switch {
        margin-top: 5px;
      }

      .form-input {
        margin-bottom: 10px;
      }
    }

    .btns-container {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;

      margin-top: 25px;
      padding: 20px 10px;
      width: 100%;

      background-color: ${(props) => props.theme.UITheme.dividerColor};

      box-sizing: border-box;

      width: 100%;

      .margined-btn {
        margin-right: 10px;
      }

      .disable-btn {
        margin-right: 10px;
      }
    }
  }
`
// ----------------------------------------------------------------------------

export default StyledCreateUserForm
